import { useCallback, useState } from "react";
import translations from "translations";
import { MatrixParametersData, TimePowerParameters } from "pages/UploadCertificate/types";
import { MatrixSection } from "./matrixDataUtils";
import { subscript } from "components/mlpaUiComponents/LegalNote/lib";

const FIELDS = [
  "taValue",
  "pMinDyn",
  "pMaxDyn",
  "pGrenzNegMin",
  "pGrenzNegMax",
  "pGrenzPosMin",
  "pGrenzPosMax",
] as const;

const isEmpty = (value: string | number | null | undefined): boolean =>
  value === "" || value == null || value === undefined;

const isZero = (value: string | number | null | undefined): boolean => {
  if (isEmpty(value)) return false;
  return Number(value) === 0;
};

/** Row + column index bundle to stay within max-params=3 */
export interface FieldIndices {
  index?: number;
  zwIndex?: number;
}

export function useMatrixValidation() {
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const getFieldKey = useCallback(
    (section: MatrixSection, field: keyof TimePowerParameters, indices?: FieldIndices) => {
      const { index, zwIndex } = indices ?? {};
      const base = index !== undefined ? `${section}-${index}-${String(field)}` : `${section}-${String(field)}`;
      if (field === "effectiveImpedances" && zwIndex !== undefined) return `${base}-${zwIndex}`;
      return base;
    },
    []
  );

  const getSectionDisplayName = useCallback((section: MatrixSection, index?: number) => {
    if (section === "taMin") return `T${subscript("A,min")}`; // "T_A,min";
    if (section === "taMax") return `T${subscript("A,max")}`; // "T_A,max";
    if (section === "taRange" && index !== undefined) return `T${subscript(`A,${index + 1}`)}`; // "T_A,1", "T_A,2", ...
    return section;
  }, []);

  const validateRow = useCallback(
    (section: MatrixSection, data: TimePowerParameters, index?: number) => {
      const emptyFields = FIELDS.filter((field) => isEmpty(data[field]));
      const zeroFields = FIELDS.filter((field) => isZero(data[field]));
      const filledFields = FIELDS.filter((field) => !isEmpty(data[field]) && !isZero(data[field]));
      const sectionDisplay = getSectionDisplayName(section, index);

      // Impedances are now booleans (checkboxes) — no per-row impedance validation needed
      const allEmpty = filledFields.length === 0;

      if (allEmpty && emptyFields.length === FIELDS.length) {
        const errorMessage =
          translations.pages.certificateUpload.errorMessages.matrixParametersAllFieldsRequired.replace(
            "{section}",
            sectionDisplay
          );
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          FIELDS.forEach((field) => {
            newErrors[getFieldKey(section, field, { index })] = errorMessage;
          });
          return newErrors;
        });
        return false;
      }

      setValidationErrors((prev) => {
        const newErrors = { ...prev };

        // Validate standard fields
        emptyFields.forEach((field) => {
          newErrors[getFieldKey(section, field, { index })] =
            translations.pages.certificateUpload.errorMessages.matrixParametersAllFieldsRequired.replace(
              "{section}",
              sectionDisplay
            );
        });

        zeroFields.forEach((field) => {
          newErrors[getFieldKey(section, field, { index })] =
            translations.pages.certificateUpload.errorMessages.matrixParametersZeroNotAllowed.replace(
              "{section}",
              sectionDisplay
            );
        });

        FIELDS.forEach((field) => {
          if (!isEmpty(data[field]) && !isZero(data[field])) {
            delete newErrors[getFieldKey(section, field, { index })];
          }
        });

        return newErrors;
      });

      return emptyFields.length === 0 && zeroFields.length === 0;
    },
    [getFieldKey, getSectionDisplayName]
  );

  const hasFieldError = useCallback(
    (section: MatrixSection, field: keyof TimePowerParameters, indices?: FieldIndices) => {
      return !!validationErrors[getFieldKey(section, field, indices)];
    },
    [validationErrors, getFieldKey]
  );

  const getFieldErrorText = useCallback(
    (section: MatrixSection, field: keyof TimePowerParameters, indices?: FieldIndices) => {
      return validationErrors[getFieldKey(section, field, indices)] || "";
    },
    [validationErrors, getFieldKey]
  );

  const hasRowError = useCallback(
    (section: MatrixSection, index?: number) => {
      return FIELDS.some((field) => hasFieldError(section, field, { index }));
    },
    [hasFieldError]
  );

  const getRowErrorText = useCallback(
    (section: MatrixSection, index?: number) => {
      for (const field of FIELDS) {
        const errorText = getFieldErrorText(section, field, { index });
        if (errorText) return errorText;
      }
      return "";
    },
    [getFieldErrorText]
  );

  const validateTAOrdering = useCallback(
    (data: MatrixParametersData) => {
      // Only check taRange rows — taMin and taMax are derived from the lowest/highest taRange
      // values so they will always match a range row by design.
      const allEntries: { value: number; section: MatrixSection; index?: number }[] = [];

      (data.taRange ?? []).forEach((row, index) => {
        if (row.taValue != null && row.taValue !== "" && row.taValue !== 0) {
          const numValue = Number(row.taValue);
          if (!isNaN(numValue)) allEntries.push({ value: numValue, section: "taRange", index });
        }
      });

      // Find duplicate values
      const seen = new Map<number, number>();
      allEntries.forEach(({ value }) => seen.set(value, (seen.get(value) ?? 0) + 1));
      const duplicates = new Set<number>(
        Array.from(seen.entries())
          .filter(([, count]) => count > 1)
          .map(([v]) => v)
      );

      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        // Clear any stale ordering errors from taRange taValue fields
        Object.keys(newErrors).forEach((key) => {
          if (
            key.startsWith("taRange-") &&
            key.endsWith("-taValue") &&
            (newErrors[key]?.includes("ascending order") || newErrors[key]?.includes("aufsteigender Reihenfolge"))
          ) {
            delete newErrors[key];
          }
        });
        // Clear any stale duplicate errors from all taValue fields
        allEntries.forEach(({ section, index }) => {
          const key = getFieldKey(section, "taValue", { index });
          if (newErrors[key] === translations.pages.certificateUpload.errorMessages.matrixParametersDuplicateTaValue) {
            delete newErrors[key];
          }
        });
        // Set duplicate errors
        allEntries.forEach(({ value, section, index }) => {
          if (duplicates.has(value)) {
            newErrors[getFieldKey(section, "taValue", { index })] =
              translations.pages.certificateUpload.errorMessages.matrixParametersDuplicateTaValue;
          }
        });
        return newErrors;
      });
    },
    [getFieldKey]
  );

  /** Run full validation pass across all taRange rows */
  const validateAllRows = useCallback(
    (formData: MatrixParametersData) => {
      formData.taRange?.forEach((rangeItem, index) => {
        if (index === 0) {
          validateRow("taRange", rangeItem, index);
        } else {
          const hasAnyValue = FIELDS.some((f) => !isEmpty(rangeItem[f]));
          if (hasAnyValue) {
            validateRow("taRange", rangeItem, index);
          } else {
            setValidationErrors((prev) => {
              const newErrors = { ...prev };
              FIELDS.forEach((field) => {
                delete newErrors[getFieldKey("taRange", field, { index })];
              });
              return newErrors;
            });
          }
        }
      });
    },
    [validateRow, getFieldKey]
  );

  /** Get all Zw column-level value errors for display */
  const getZwErrors = useCallback((formData: MatrixParametersData) => {
    const zwErrors: string[] = [];

    (formData.zwValues ?? []).forEach((val, zwIdx) => {
      if (zwIdx > 0 && isEmpty(val)) {
        zwErrors.push(
          `${translations.pages.certificateUpload.errorMessages.matrixParametersAllFieldsRequired.replace(
            "{section}",
            `Z${subscript(`w,${zwIdx + 1}`)}`
          )}`
        );
      } else if (zwIdx > 0 && isZero(val)) {
        zwErrors.push(
          `Z${subscript(`w,${zwIdx + 1}`)}: ${
            translations.pages.certificateUpload.errorMessages.matrixParametersZwMustBeGreaterThanZero
          }`
        );
      }
    });
    return zwErrors;
  }, []);

  return {
    validationErrors,
    setValidationErrors,
    getFieldKey,
    validateRow,
    hasFieldError,
    getFieldErrorText,
    hasRowError,
    getRowErrorText,
    validateTAOrdering,
    validateAllRows,
    getZwErrors,
  };
}
